import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBackOutlined,
  SaveOutlined,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/useAuthStore';
import { CorpsMetier, CORPS_METIER_LABELS } from '../types';
import { useNotification } from '../contexts/NotificationContext';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();
  const { showSuccess, showError } = useNotification();

  const [formData, setFormData] = useState({
    nom: user?.nom || '',
    prenom: user?.prenom || '',
    email: user?.email || '',
    emailProfessionnel: user?.emailProfessionnel || '',
    entreprise: user?.entreprise || '',
    siret: user?.siret || '',
    adresse: user?.adresse || '',
    telephone: user?.telephone || '',
    siren: user?.siren || '',
    codePaysIso: user?.codePaysIso || 'FR',
    numTvaIntracom: user?.numTvaIntracom || '',
    corpsMetier: user?.corpsMetier || CorpsMetier.CARRELEUR,
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    console.log('📝 Début de la mise à jour du profil...', formData);

    try {
      // Mettre à jour l'utilisateur
      await updateUser(formData);

      console.log('✅ Profil mis à jour avec succès');
      showSuccess('Profil mis à jour avec succès');
    } catch (error: any) {
      console.error('❌ Erreur lors de la mise à jour du profil:', error);
      showError(error.message || 'Erreur lors de la mise à jour du profil');
    } finally {
      setIsSaving(false);
      console.log('📝 Fin de la mise à jour, isSaving:', false);
    }
  };

  if (!user) {
    return (
      <Box>
        <Alert severity="error">Vous devez être connecté pour accéder à cette page</Alert>
        <Button onClick={() => navigate('/login')} sx={{ mt: 2 }}>
          Se connecter
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937' }}>
          Éditer mon profil
        </Typography>
        <Button
          startIcon={<ArrowBackOutlined />}
          onClick={() => navigate(-1)}
          variant="outlined"
          sx={{
            borderColor: '#d1d5db',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#9ca3af',
              bgcolor: '#f9fafb',
            },
          }}
        >
          Retour
        </Button>
      </Box>

      {/* Formulaire */}
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Protection de votre période d'essai :</strong> Le SIRET et SIREN ne peuvent pas être modifiés
          car ils sont liés à votre période d'essai Premium. Si vous avez changé d'entreprise,
          veuillez contacter le support.
        </Alert>

        <form onSubmit={handleSubmit}>
          {/* Informations personnelles */}
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1f2937', mb: 3 }}>
            Informations personnelles
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prénom"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email de connexion"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                variant="outlined"
                helperText="Utilisé pour vous connecter à l'application"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Informations entreprise */}
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1f2937', mb: 3 }}>
            Informations entreprise
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom de l'entreprise"
                name="entreprise"
                value={formData.entreprise}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SIRET"
                name="siret"
                value={formData.siret}
                onChange={handleChange}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                }}
                helperText="Le SIRET ne peut pas être modifié (lié à votre période d'essai)"
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f9fafb',
                    cursor: 'not-allowed',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email professionnel"
                name="emailProfessionnel"
                type="email"
                value={formData.emailProfessionnel}
                onChange={handleChange}
                variant="outlined"
                placeholder="votre-email@entreprise.fr"
                helperText="Email utilisé comme expéditeur sur vos devis et factures"
              />
            </Grid>

            {/* Corps de métier */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Corps de métier</InputLabel>
                <Select
                  name="corpsMetier"
                  value={formData.corpsMetier}
                  onChange={handleSelectChange}
                  label="Corps de métier"
                >
                  {Object.entries(CORPS_METIER_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Champs Factur-X */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, color: '#757575', fontWeight: 600 }}>
                Informations légales (Factur-X)
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SIREN (optionnel)"
                name="siren"
                value={formData.siren}
                onChange={handleChange}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                }}
                helperText="Le SIREN ne peut pas être modifié (lié à votre SIRET)"
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 14,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f9fafb',
                    cursor: 'not-allowed',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Code Pays ISO"
                name="codePaysIso"
                value={formData.codePaysIso}
                onChange={handleChange}
                variant="outlined"
                placeholder="FR"
                inputProps={{
                  maxLength: 2,
                  style: { textTransform: 'uppercase' },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="N° TVA Intracommunautaire (optionnel)"
                name="numTvaIntracom"
                value={formData.numTvaIntracom}
                onChange={handleChange}
                variant="outlined"
                placeholder="FR12345678901"
                helperText="Ex: FR12345678901"
                inputProps={{
                  maxLength: 15,
                  style: { textTransform: 'uppercase' },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>

          {/* Boutons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 5 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={isSaving}
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#9ca3af',
                  bgcolor: '#f9fafb',
                },
              }}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveOutlined />}
              disabled={isSaving}
              sx={{
                bgcolor: '#3b82f6',
                color: 'white',
                px: 3,
                py: 1,
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
                '&:hover': {
                  bgcolor: '#2563eb',
                  boxShadow: '0 4px 6px rgba(59, 130, 246, 0.3)',
                },
                '&:disabled': {
                  bgcolor: '#e5e7eb',
                  color: '#9ca3af',
                  boxShadow: 'none',
                },
              }}
            >
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ProfilePage;
